import { useInventoryItem } from "../../hooks/useInventory";
import { Card, CardContent, CardHeader, CardTitle } from "@tonic/shared/components/ui";
import { Badge } from "@tonic/shared/components/ui";
import { formatDistanceToNow } from "date-fns";
import PropTypes from 'prop-types';

/**
 * @typedef {Object} ItemHistoryProps
 * @property {string} itemId - ID of the inventory item
 */

/**
 * Component that displays the history of an inventory item
 * @param {ItemHistoryProps} props
 * @returns {JSX.Element} The item history component
 */
export function ItemHistory({ itemId }) {
  const { data: item } = useInventoryItem(itemId);

  if (!item?.history?.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>History</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">No history available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>History</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-8">
          {item.history.map((event, index) => (
            <div key={event.id} className="relative pb-8">
              {index < item.history.length - 1 && (
                <span
                  className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                  aria-hidden="true"
                />
              )}
              <div className="relative flex space-x-3">
                <div>
                  <span
                    className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${
                      event.type === "status_change"
                        ? "bg-blue-500"
                        : event.type === "transfer"
                        ? "bg-green-500"
                        : event.type === "update"
                        ? "bg-yellow-500"
                        : "bg-gray-500"
                    }`}
                  >
                    {event.type === "status_change" ? (
                      <TagIcon className="h-5 w-5 text-white" />
                    ) : event.type === "transfer" ? (
                      <TruckIcon className="h-5 w-5 text-white" />
                    ) : event.type === "update" ? (
                      <PencilIcon className="h-5 w-5 text-white" />
                    ) : (
                      <CircleIcon className="h-5 w-5 text-white" />
                    )}
                  </span>
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium">{event.description}</h3>
                    <time className="text-sm text-gray-500">
                      {formatDistanceToNow(new Date(event.timestamp), {
                        addSuffix: true,
                      })}
                    </time>
                  </div>
                  {event.type === "status_change" && (
                    <div className="flex items-center space-x-2">
                      <Badge variant="outline">{event.oldStatus}</Badge>
                      <span>→</span>
                      <Badge>{event.newStatus}</Badge>
                    </div>
                  )}
                  {event.type === "transfer" && (
                    <div className="text-sm text-gray-500">
                      From: {event.sourceLocation} • To: {event.destinationLocation}
                    </div>
                  )}
                  {event.metadata && (
                    <pre className="mt-2 text-sm text-gray-500 whitespace-pre-wrap">
                      {JSON.stringify(event.metadata, null, 2)}
                    </pre>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

ItemHistory.propTypes = {
  itemId: PropTypes.string.isRequired,
}; 